import { ONBOARDING_FIELDS } from "@fitties/shared";
import { getNextOnboardingStep } from "@schemas/users.validators";
import { useCurrentUser } from "@shared/queries";

/**
 * Custom hook for derived onboarding field state.
 *
 * Provides the current onboarding field, progress, and related state
 * based on the user's data completeness. Fully derived from user data.
 * Represents linear progression through required profile fields.
 *
 * @returns Object containing current field, progress, and completion state
 *
 * @example
 * ```tsx
 * function OnboardingPage() {
 *   const { currentField, fieldProgress, canProceed } = useOnboardingField();
 *   return <div>Current field: {currentField}</div>;
 * }
 * ```
 */
export default function useOnboardingField() {
  const { user } = useCurrentUser();

  // Derive current field from user data
  const currentField = user ? getNextOnboardingStep(user) : "display_name";

  // Calculate field progress (0-1)
  const currentFieldIndex = currentField ? ONBOARDING_FIELDS.indexOf(currentField) : ONBOARDING_FIELDS.length;
  const fieldProgress = currentFieldIndex / ONBOARDING_FIELDS.length;

  // Determine if user can proceed (has a field to complete)
  const canProceed = currentField !== null;

  // Calculate total fields completed
  const fieldsCompleted = currentFieldIndex;
  const totalFields = ONBOARDING_FIELDS.length;

  return {
    currentField,
    fieldProgress,
    canProceed,
    fieldsCompleted,
    totalFields,
    currentFieldIndex,
  };
}
